import { Center, Loader } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { Navigate } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { User } from "oidc-client-ts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { authProfileAtom, store, userAtom, workosAuthAtom } from "~/atoms";
import { bootErrorMessageKey, useAppBootstrap } from "~/hooks/useAppBootstrap";
import { useBuildChangeAccessRefresh } from "~/hooks/useBuildChangeAccessRefresh";
import { useReloadOnStaleBuild } from "~/hooks/useReloadOnStaleBuild";
import { useSilentRefresh } from "~/hooks/useSilentRefresh";
import { isWorkOSAuth, redirectToAuthkitLogin } from "~/lib/authProvider";
import { AuthkitError, getSession } from "~/lib/authkitClient";
import { resetSessionAtoms } from "~/lib/sessionAtoms";
import { DEMO_AUTH_STORAGE_KEY } from "~/providers/auth/state";
import { Env } from "~/utils/Env";

import AuthErrorPanel from "./AuthErrorPanel";
import PublicPageContainer from "./PublicPageContainer";
import TermsOfServiceGate from "./TermsOfServiceGate";

type WorkosBootStatus = "loading" | "ready" | "redirect" | "error";

/**
 * Restore the in-memory `authProfileAtom` from the persisted
 * `userAtom` on reload. Both the WorkOS mock-mode short-circuit and
 * the Zitadel branch enter PrivateRoutes with `userAtom` already
 * hydrated by `atomWithStorage`/`loadScenario` but with
 * `authProfileAtom` empty (it deliberately doesn't persist — XSS
 * hygiene). Nav reads `authProfileAtom.displayName` so we seed it
 * once per session.
 */
function seedAuthProfileFromUserAtom(user: User): void {
  if (store.get(authProfileAtom)) return;
  const p = user.profile ?? {};
  const firstName = (p.given_name as string | undefined) ?? "";
  const lastName = (p.family_name as string | undefined) ?? "";
  store.set(authProfileAtom, {
    id: (p.sub as string | undefined) ?? "",
    email: (p.email as string | undefined) ?? "",
    firstName,
    lastName,
    displayName:
      (p.name as string | undefined) ?? `${firstName} ${lastName}`.trim(),
    mongoId: p.mongoId as string | undefined,
  });
}

type PrivateRoutesProps = { children: React.ReactNode };

const BootLoader = () => (
  <PublicPageContainer>
    <Center mih={400}>
      <Loader size="lg" />
    </Center>
  </PublicPageContainer>
);

const PrivateRoutes = ({ children }: PrivateRoutesProps) => {
  // Lives here, above the auth-path split, so one instance watches for a newer
  // deploy for as long as the private layout is mounted (INS-1968).
  useReloadOnStaleBuild();

  if (isWorkOSAuth())
    return <WorkosPrivateRoutes>{children}</WorkosPrivateRoutes>;
  return <ZitadelPrivateRoutes>{children}</ZitadelPrivateRoutes>;
};

const ZitadelPrivateRoutes = ({ children }: PrivateRoutesProps) => {
  const user = useAtomValue(userAtom);
  const hasToken = !!user?.access_token;
  // No boot runs on protected routes here (Login populated the atoms), so the
  // build-change access refresh is the only thing between a cached session
  // and the entitlements the server grants today.
  const accessRefresh = useBuildChangeAccessRefresh(hasToken);

  if (user) seedAuthProfileFromUserAtom(user);
  if (!hasToken) return <Navigate to="/" replace />;
  if (accessRefresh === "redirect") return <Navigate to="/" replace />;
  if (accessRefresh === "refreshing") return <BootLoader />;
  return <>{children}</>;
};

const WorkosPrivateRoutes = ({ children }: PrivateRoutesProps) => {
  const { t } = useTranslation();
  const authState = useAtomValue(workosAuthAtom);
  const setAuthState = useSetAtom(workosAuthAtom);
  const [status, setStatus] = useState<WorkosBootStatus>(
    authState.user ? "ready" : "loading",
  );
  const attempted = useRef(false);
  const [hydrateAttempt, setHydrateAttempt] = useState(0);

  // Proactive silent refresh (N7 visibilitychange + N8 expiry timer). Self-
  // gates on the hydrated WorkOS session, so it's inert during loading, in
  // mock mode, and on the legacy path.
  useSilentRefresh();

  useEffect(() => {
    if (attempted.current || authState.user) return;
    attempted.current = true;

    // Mock mode: hydrate from localStorage (mirrors AuthProvider's flow)
    // without a cross-origin /api/session call. Used by Playwright and
    // `pnpm dev:mock` so the SPA boots without authkit-ui running.
    if (Env.VITE_MOCK === "true") {
      const stored = window.localStorage.getItem(DEMO_AUTH_STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as {
            user?: { id?: string };
            organizationId?: string | null;
          };
          setAuthState({
            user: { id: parsed.user?.id ?? "mock-user" },
            orgId: parsed.organizationId ?? null,
            csrfToken: null,
            authGen: 0,
            expiresAt: null,
          });
          setStatus("ready");
          return;
        } catch {
          window.localStorage.removeItem(DEMO_AUTH_STORAGE_KEY);
        }
      }
      setStatus("redirect");
      return;
    }

    void (async () => {
      try {
        const session = await getSession();
        if (!session) {
          resetSessionAtoms();
          setStatus("redirect");
          return;
        }
        setAuthState({
          user: session.user,
          orgId: session.organizationId,
          csrfToken: session.csrfToken,
          authGen: 0,
          expiresAt: session.expiresAt,
        });
        setStatus("ready");
      } catch (err) {
        if (err instanceof AuthkitError && err.status === 401) {
          resetSessionAtoms();
          setStatus("redirect");
          return;
        }
        Sentry.captureException(err, {
          tags: { event: "auth.session_hydrate.failure" },
        });
        setStatus("error");
      }
    })();
  }, [authState.user, setAuthState, hydrateAttempt]);

  if (status === "redirect") {
    if (Env.VITE_MOCK === "true") {
      return <Navigate to="/" replace />;
    }
    redirectToAuthkitLogin({
      returnTo: window.location.pathname + window.location.search,
    });
    return null;
  }

  if (status === "error") {
    return (
      <AuthErrorPanel
        message={t("authErrors.sessionCheckFailed")}
        onRetry={() => {
          attempted.current = false;
          setStatus("loading");
          setHydrateAttempt((attempt) => attempt + 1);
        }}
      />
    );
  }

  if (status === "loading") {
    return <BootLoader />;
  }

  // Mock mode pre-populates orgsAtom/userConfigAtom/userAtom via
  // the existing MEQ-demo-auth-state hydration in main.tsx's AuthProvider
  // (and Playwright's localStorage seed). The app-boot data load only
  // runs against real authkit-ui + v2-server — skipping it in mock mode
  // keeps the existing Playwright suite untouched. We still need to
  // restore authProfileAtom from the hydrated userAtom so Nav has a
  // display name to render.
  if (Env.VITE_MOCK === "true") {
    const u = store.get(userAtom);
    if (u) seedAuthProfileFromUserAtom(u);
    return <>{children}</>;
  }

  return <AppBootstrapGate>{children}</AppBootstrapGate>;
};

/**
 * Bootstrap gate for the WorkOS path. Runs `useAppBootstrap` after
 * `workosAuthAtom` is hydrated and only renders the routed children
 * once the orgs/userConfig/userAtom atoms are populated and the Terms
 * of Service are accepted (authkit returns deep links here, skipping
 * Login's own check). Multi-org users do NOT see a placeholder here —
 * the SelectOrg dialog is owned
 * by Login (which mounts the same hook); arrival on a private route in
 * a multi-org state means something went wrong upstream, so we render
 * the loader rather than silently dropping the user.
 */
const AppBootstrapGate = ({ children }: PrivateRoutesProps) => {
  const { t } = useTranslation();
  const boot = useAppBootstrap();
  // Mounted alongside the boot so "is there a cached session to refresh?" is
  // decided before the boot could populate the atoms itself; a from-scratch
  // boot then owes no second fetch.
  const accessRefresh = useBuildChangeAccessRefresh(boot.status === "ready");

  if (boot.status === "ready") {
    if (accessRefresh === "redirect") return <Navigate to="/" replace />;
    if (accessRefresh === "refreshing") return <BootLoader />;
    return (
      <TermsOfServiceGate fallback={<BootLoader />}>
        {children}
      </TermsOfServiceGate>
    );
  }
  // Multi-org users hitting a private route directly (deep link or
  // first-load after authkit redirect) bounce to Login so the
  // SelectOrg dialog can drive the choice. Login owns the dialog UI.
  if (boot.status === "needsOrgSelection") {
    return (
      <Navigate
        to="/"
        search={{
          returnTo: window.location.pathname + window.location.search,
        }}
        replace
      />
    );
  }
  if (boot.status === "error") {
    return <AuthErrorPanel message={t(bootErrorMessageKey(boot.error))} />;
  }
  return <BootLoader />;
};

export default PrivateRoutes;
